import React, { useState, useEffect, useContext } from "react";
import Modal from "react-bootstrap/Modal";
import axios from "axios";
import { toast } from "react-toastify";
import moment from "moment";
import Swal from "sweetalert2";
import { AuthContext } from "../../../component/context/AuthContext";
import DivSpinner from "../../../component/utilities/Spinner";
import { HOST, GET_EMP_BY_ID } from "../../../component/config/Constant";
import * as CONST from "../../../component/config/Constant";
import '../employee/employee-details.css';
import DataTable from "react-data-table-component";

const ITCheck = ({ resignationData, employeeId, closeItCheckModal }) => {
  const [authUserData] = useContext(AuthContext);
  const [assets, setAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [comment, setComment] = useState("");
  const [empDetails, setEmpDetails] = useState(null);
  const [errors, setErrors] = useState({});

  const isToken = authUserData?.[0]?.token || "";

  useEffect(() => {
    if (employeeId) {
      fetchInitialData();
    } else {
      setLoading(false);
    }
  }, [employeeId, isToken]); // Re-run if token eventually loads

  const fetchInitialData = async () => {
    if (!isToken) {
      return;
    }

    setLoading(true);
    try {
      const [empRes, assetRes] = await Promise.all([
        axios.post(
          CONST.GET_EMP_BY_ID,
          { id: authUserData?.[0]?.user_id, user_id: employeeId },
          { headers: { Authorization: `Bearer ${isToken}` } },
        ),
        axios.get(`${CONST.RESIGNATION_ASSET_LIST}${employeeId}`, {
          headers: { Authorization: `Bearer ${isToken}` },
        }),
      ]);

      if (empRes.data.status === "success") setEmpDetails(empRes.data.data);
      if (assetRes.data.status === "success")
        setAssets(assetRes.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || "Server communication failed");
    } finally {
      setLoading(false);
    }
  };
  const toggleAsset = (id) => {
    setSelectedAssets((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectedAssets.length === assets.length) {
      setSelectedAssets([]);
    } else {
      setSelectedAssets(assets.map((a) => a.id));
    }
  };

  const handleAction = async (action) => {
    setErrors({});
    const validationErrors = {};

    // 1. Comment Validation (Inline)
    if (!comment || !comment.trim()) {
      validationErrors.comment = "Please provide an assessment comment.";
    }

    // If validation fails, set errors and stop
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (
      action === "Approved" &&
      assets.length > 0 &&
      selectedAssets.length < assets.length
    ) {
      const result = await Swal.fire({
        title: "Missing Items?",
        text: "Not all assets are checked. Are you sure you want to approve IT clearance?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Approve anyway",
        confirmButtonColor: "#28a745",
      });
      if (!result.isConfirmed) return;
    }
    setProcessing(true);
    try {
      const payload = {
        resignation_id: resignationData?.id,
        step_number: 3, // IT Clearance Step
        action: action,
        comment: comment,
        employee_id: employeeId,
        asset_ids: selectedAssets,
      };

      const response = await axios.post(
        CONST.RESIGNATION_ITCHECK.replace(":employee_id", employeeId),
        payload,
        {
          headers: { Authorization: `Bearer ${isToken}` },
        },
      );

      if (response.data.status === "success") {
        toast.success(`IT Clearance ${action} successfully`);
        closeItCheckModal();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Internal Server Error");
    } finally {
      setProcessing(false);
    }
  };
  const assetColumns = [
    {
      name: "Status",
      cell: (row) => (
        <input
          type="checkbox"
          checked={selectedAssets.includes(row.id)}
          onChange={() => toggleAsset(row.id)}
        />
      ),
      width: "80px",
    },
    {
      name: "Asset Detail",
      cell: (row) => (
        <>
          <div className="fw-bold text-dark">
            {row.eassetType?.name || row.e_asset_type?.name || "Asset"}
          </div>
          <small className="text-muted">
            ID: {row.e_asset_type?.id || "N/A"}
          </small>
        </>
      ),
    },
    {
      name: "Brand/Model",
      selector: (row) => row.e_brand?.name || "N/A",
    },
    {
      name: "Issued On",
      selector: (row) =>
        moment(row.allocated_date).format("DD MMM YYYY"),
    },
  ];
  return (
    <Modal
      show={true}
      onHide={closeItCheckModal}
      fullscreen={true}
      backdrop="static"
      centered
      contentClassName="border-0 rounded-0"
    >
      <Modal.Header closeButton className="bg-white border-bottom px-4 py-3">
        <Modal.Title className="fw-bold text-dark d-flex align-items-center">
          <div className="bg-primary-subtle p-2 rounded-3 me-3 mr-3 text-primary">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          </div>
          IT Asset Clearance: {empDetails?.first_name} {empDetails?.last_name}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: '#f8fafc', padding: 0 }}>
        {loading ? (
          <div className="text-center py-5">
            <DivSpinner size="lg" color="primary" />
          </div>
        ) : (
          <div className="container-fluid p-4">
            <div className="row g-4">
              {/* Left Column: Employee Snapshot */}
              <div className="col-lg-4">
                <div className="card h-auto" style={{ borderRadius: 14, border: '1px solid #e2e8f0' }}>
                  {empDetails ? (
                    <div style={{ padding: '20px' }}>
                        {/* Profile Row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                          <div style={{ width: 56, height: 56, minWidth: 56, borderRadius: '50%', overflow: 'hidden', background: '#0f2847', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7db3ff', fontWeight: 700, fontSize: '1rem' }}>
                            {empDetails?.profile_photo_path ? (
                              <img src={HOST + empDetails.profile_photo_path} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <>{empDetails?.first_name?.[0]}{empDetails?.last_name?.[0]}</>
                            )}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0a1628' }}>{empDetails?.first_name} {empDetails?.last_name}</div>
                            <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{empDetails?.designation?.name} | {empDetails?.department?.name}</div>
                            <span style={{ background: '#f0f5ff', color: '#1167e8', padding: '2px 10px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 600 }}>EMP-{empDetails?.id}</span>
                          </div>
                        </div>

                        {/* Info Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                          <div>
                            <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Release Date</div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: resignationData?.release_date ? '#059669' : '#d97706' }}>{resignationData?.release_date ? moment(resignationData.release_date).format("DD MMM YYYY") : "Not Set"}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.68rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Asset Count</div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#0a1628' }}>{assets.length} Items</div>
                          </div>
                        </div>
                    </div>
                  ) : (
                    <div className="text-center p-4">
                      <DivSpinner size="sm" color="primary" />
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Inventory Checklist & Actions */}
                  <div className="col-lg-8">
                    <div className="row g-4">
                      {/* Asset Table Card */}
                      <div className="col-12">
                        <div className="card h-100 overflow-hidden" style={{ borderRadius: 14, border: '1px solid #e2e8f0' }}>
                          <div className="card-header bg-white p-4 border-bottom d-flex justify-content-between align-items-center">
                            <h5 className="mb-0 fw-bold text-dark">
                              Physical Return Verification
                            </h5>
                            {assets.length > 0 && (
                              <button
                                className="btn btn-sm btn-primary rounded-pill px-3 shadow-sm"
                                onClick={handleSelectAll}
                              >
                                {selectedAssets.length === assets.length
                                  ? "Deselect All"
                                  : "Select All"}
                              </button>
                            )}
                          </div>
                          <div className="card-body p-0">
                            <div className="table-responsive">
                              <DataTable
                                columns={assetColumns}
                                data={assets}
                                highlightOnHover
                                pointerOnHover
                                noDataComponent={
                                  <div className="py-5 text-success fw-bold">
                                    No active company assets found
                                  </div>
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action Hub */}
                      <div className="col-12">
                        <div className="card p-4 bg-white" style={{ borderRadius: 14, border: '1px solid #e2e8f0' }}>
                          <div className="d-flex align-items-center mb-4">
                            <div className="bg-primary-subtle p-2 rounded-3 me-3 mr-3 text-primary">
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </div>
                            <h5 className="fw-bold mb-0 text-dark ls-1 text-uppercase small">IT Admin Assessment</h5>
                          </div>

                          <div className="form-group mb-4">
                            <textarea
                              className={`form-control border-opacity-25 ${errors.comment ? "is-invalid" : ""
                                }`}
                              rows="4"
                              style={{ borderRadius: '1rem' }}
                              placeholder="Note condition of returned items (e.g., Laptop screen intact, charger returned)..."
                              value={comment}
                              onChange={(e) => {
                                setComment(e.target.value);
                                // Optional: Clear error as user types
                                if (errors.comment) setErrors((prev) => ({ ...prev, comment: null }));
                              }}
                            ></textarea>
                            {/* Inline Error Message */}
                            {errors.comment && (
                              <div className="invalid-feedback d-block ps-2">
                                {errors.comment}
                              </div>
                            )}
                          </div>

                          <div className="d-flex gap-3 justify-content-end">
                            <button
                              className="btn btn-md px-4 mr-2"
                              style={{ background: '#059669', color: '#fff', borderRadius: 8, fontWeight: 600 }}
                              onClick={() => handleAction("Approved")}
                              disabled={processing}
                            >
                              {processing ? "Processing..." : "Approve IT Clearance"}
                            </button>
                            <button
                              className="btn btn-md px-4"
                              style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, fontWeight: 600 }}
                              onClick={() => handleAction("Rejected")}
                              disabled={processing}
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
        )}
            </Modal.Body>
          </Modal>
        );
};

        export default ITCheck;
